import type * as models from 'pkg/api/models';
import type { AllowedActionable } from 'pkg/api/models/allowed_actionable';
import type { Dateable } from 'pkg/api/models/dateable';
import type { TaxDetail } from 'pkg/api/models/payment_preview';
import type { Record } from 'pkg/api/models/record';
import type { User } from 'pkg/api/models/user';

export enum TransactionExportTypes {
	PaidBased = '1',
	SentBased = '0',
}

export interface ExportOrdersPayload {
	recipientName?: boolean;
	recipientUserId?: boolean;
	email?: boolean;
	assignedContacts?: boolean;
	currency?: boolean;
	amountDue?: boolean;
	invoiceNumber?: boolean;
	externalId?: boolean;
	status?: boolean;
	createdAt?: boolean;
	dueDate?: boolean;
	paidAt?: boolean;
	type?: boolean;
	companyName?: boolean;
	addressData?: boolean;
	products?: boolean;
	productMetadata?: boolean;
	orderId?: boolean;
	subscriptionId?: boolean;
}

export interface ExportTransactionsPayload {
	from: number;
	to: number;
	exportType: number;
}

export enum Statuses {
	Draft = 'draft',
	Open = 'open',
	PaymentProcessing = 'payment_processing',
	Paid = 'paid',
	PastDue = 'past_due',
	Void = 'void',
	Uncollectible = 'uncollectible',
	Refunded = 'refunded',
}

export enum OrderType {
	Unknown = 'unknown',
	Invoice = 'invoice',
	Payment = 'payment',
}

enum OrderJobStatus {
	Unknown = 'unknown',
	Processing = 'processing',
	Failed = 'failed',
}

export enum InvopopEntryStatus {
	Unknown = 'unknown',
	Approved = 'approved',
	Pending = 'pending',
	Invalid = 'invalid',
}

interface InvopopEntryResponse {
	siloEntryId: string;
	status: InvopopEntryStatus;
	taxAuthorityType: string;
	invoiceUrl?: string;
	id: number;
}

export interface OrderSummaryRowDetail {
	totalListPrice: number;
	totalPrice: number;
	taxAmount: number;
}

export interface OrderSummary {
	subtotal: number;
	excludedTax: number;
	totalExcludingTax: number;
	taxes: TaxDetail[];
	rowDetails: { [rowId: number]: OrderSummaryRowDetail };
}

export interface Order extends Record, Dateable, AllowedActionable {
	publicId: string;
	accountId: number;
	address: string;
	amountDue: number;
	amountRemaining: number;
	city: string;
	companyName: string;
	country: string;
	currency: string;
	customerUserId: number;
	description: string;
	dueDate: number;
	email: string;
	firstName: string;
	flags: string[];
	group: models.group.Group;
	groupId: number;
	lastName: string;
	phoneNumber: string;
	postalCode: string;
	region: string;
	sentAt: number;
	paidAt: number;
	jobStatus: OrderJobStatus;
	stripeCouponId: number;
	paymentProviderId: string;
	status: string;
	subscriptionId: number;
	scheduledAt: number;
	invoiceNumber: number;
	type: OrderType;
	paymentUrl?: string;
	amountRefunded?: number;
	refundedAt?: number;
	dueDays: number;
	refundInProgress: boolean;
	paymentProcessing: boolean;
	remainingRefundable?: number;
	// Every payment the order took, refunded or not; more than amountDue when it was paid twice.
	collectedGross?: number;
	invopopEntry?: InvopopEntryResponse[];

	creditNotes?: models.creditNote.CreditNote[];
	customerUser?: User;
	subscription?: models.subscription.Subscription;
	userProducts: models.userProduct.UserProduct[];
	rows: models.orderRow.OrderRow[];
	refunds: models.orderRefund.OrderRefund[];
	charges?: models.orderCharge.OrderChange[];
	summary?: OrderSummary;
}
